/**
 * System Health Dashboard Component
 *
 * Real-time monitoring of system services
 */

import * as React from 'react';
import {
  Activity,
  Database,
  Server,
  CreditCard,
  Mail,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  useSystemHealth,
  useServicesAvailability,
  useRecentIncidents,
  type ServiceHealth,
} from '@/hooks/useAdmin';

const SERVICE_ICONS: Record<string, React.ElementType> = {
  database: Database,
  redis: Server,
  stripe: CreditCard,
  sendgrid: Mail,
};

const STATUS_STYLES = {
  healthy: {
    icon: CheckCircle2,
    color: 'text-green-500',
    bg: 'bg-green-500/10',
    badge: 'bg-green-500/20 text-green-700 dark:text-green-400',
  },
  degraded: {
    icon: AlertTriangle,
    color: 'text-yellow-500',
    bg: 'bg-yellow-500/10',
    badge: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400',
  },
  down: {
    icon: XCircle,
    color: 'text-red-500',
    bg: 'bg-red-500/10',
    badge: 'bg-red-500/20 text-red-700 dark:text-red-400',
  },
};

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function formatResponseTime(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  }
  return `${(ms / 1000).toFixed(2)}s`;
}

interface ServiceCardProps {
  service: ServiceHealth;
}

function ServiceCard({ service }: ServiceCardProps): React.JSX.Element {
  const Icon = SERVICE_ICONS[service.service] || Server;
  const statusStyle = STATUS_STYLES[service.status];
  const StatusIcon = statusStyle.icon;

  return (
    <Card className={cn('relative overflow-hidden', statusStyle.bg)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium capitalize">{service.service}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          <StatusIcon className={cn('h-5 w-5', statusStyle.color)} />
          <span className={cn('text-lg font-semibold capitalize', statusStyle.color)}>
            {service.status}
          </span>
        </div>
        <div className="mt-2 space-y-1 text-xs text-muted-foreground">
          <div className="flex items-center justify-between">
            <span>Response Time</span>
            <span className="font-medium">{formatResponseTime(service.responseTimeMs)}</span>
          </div>
          {service.error && (
            <div className="mt-2 rounded bg-red-500/10 p-2 text-red-600 dark:text-red-400">
              {service.error}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ServiceCardSkeleton(): React.JSX.Element {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-4" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-6 w-24" />
        <div className="mt-2 space-y-1">
          <Skeleton className="h-3 w-full" />
        </div>
      </CardContent>
    </Card>
  );
}

interface AvailabilityBarProps {
  service: string;
  availability: number;
  status: string;
}

function AvailabilityBar({ service, availability, status }: AvailabilityBarProps): React.JSX.Element {
  const statusStyle = STATUS_STYLES[status as keyof typeof STATUS_STYLES] || STATUS_STYLES.healthy;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="capitalize font-medium">{service}</span>
        <span className={cn('font-semibold', statusStyle.color)}>{availability.toFixed(2)}%</span>
      </div>
      <Progress
        value={availability}
        className={cn('h-2', availability >= 99 ? 'bg-green-100' : availability >= 95 ? 'bg-yellow-100' : 'bg-red-100')}
      />
    </div>
  );
}

interface IncidentItemProps {
  incident: {
    service: string;
    status: string;
    startTime: string;
    endTime?: string;
    duration?: number;
  };
}

function IncidentItem({ incident }: IncidentItemProps): React.JSX.Element {
  const statusStyle = STATUS_STYLES[incident.status as keyof typeof STATUS_STYLES] || STATUS_STYLES.degraded;

  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <div className="flex items-center gap-2">
        <Badge className={statusStyle.badge}>{incident.status}</Badge>
        <span className="capitalize font-medium">{incident.service}</span>
      </div>
      <div className="text-sm text-muted-foreground">
        <Clock className="inline h-3 w-3 mr-1" />
        {new Date(incident.startTime).toLocaleString()}
      </div>
    </div>
  );
}

export function SystemHealthDashboard(): React.JSX.Element {
  const { data: health, isLoading: healthLoading, refetch: refetchHealth } = useSystemHealth();
  const { data: availability, isLoading: availabilityLoading } = useServicesAvailability();
  const { data: incidents, isLoading: incidentsLoading } = useRecentIncidents();

  const overallStyle = health?.overall
    ? STATUS_STYLES[health.overall]
    : STATUS_STYLES.healthy;
  const OverallIcon = overallStyle.icon;

  return (
    <div className="space-y-6">
      {/* Overall Status Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn('rounded-full p-2', overallStyle.bg)}>
                <Activity className={cn('h-6 w-6', overallStyle.color)} />
              </div>
              <div>
                <CardTitle>System Status</CardTitle>
                <CardDescription>
                  Real-time health monitoring of all services
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {health && (
                <div className="text-right">
                  <div className="flex items-center gap-2">
                    <OverallIcon className={cn('h-5 w-5', overallStyle.color)} />
                    <span className={cn('text-lg font-semibold capitalize', overallStyle.color)}>
                      {health.overall}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Uptime: {formatUptime(health.uptime)}
                  </div>
                </div>
              )}
              <Button variant="outline" size="sm" onClick={() => refetchHealth()}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Service Status Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {healthLoading ? (
          <>
            <ServiceCardSkeleton />
            <ServiceCardSkeleton />
            <ServiceCardSkeleton />
            <ServiceCardSkeleton />
          </>
        ) : (
          health?.services.map((service) => (
            <ServiceCard key={service.service} service={service} />
          ))
        )}
      </div>

      {/* Availability & Incidents */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* 24h Availability */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">24-Hour Availability</CardTitle>
            <CardDescription>Service uptime over the last 24 hours</CardDescription>
          </CardHeader>
          <CardContent>
            {availabilityLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            ) : availability?.services ? (
              <div className="space-y-4">
                {Object.entries(availability.services).map(([service, data]) => (
                  <AvailabilityBar
                    key={service}
                    service={service}
                    availability={data.availability}
                    status={data.status}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-4">
                No availability data
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Incidents */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Incidents</CardTitle>
            <CardDescription>Issues detected in the last 24 hours</CardDescription>
          </CardHeader>
          <CardContent>
            {incidentsLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : incidents?.incidents && incidents.incidents.length > 0 ? (
              <div>
                {incidents.incidents.slice(0, 5).map((incident, idx) => (
                  <IncidentItem key={idx} incident={incident} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-2" />
                <p className="text-muted-foreground">No incidents in the last 24 hours</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SystemHealthDashboard;
